import React from 'react';
import {
  AFFILIATE_MEMBERSHIP,
  BETTER_STARTING_POINT,
  MEMBERSHIP_COST_COMPARISON,
  MEMBERSHIP_HERO,
  MEMBERSHIP_ROADMAP_NOTE,
  NEEDS_ASSESSMENT_PDF_URL,
  NEEDS_ASSESSMENT_SOURCE_LABEL,
  PUBLIC_ACCESS,
  REGULAR_MEMBERSHIP,
  WHY_AGENCIES_JOIN,
  WORKFLOW_COMPARISON,
  type WorkflowStep,
} from './homePageConstants';

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function WorkflowSteps({ steps }: { steps: readonly WorkflowStep[] }) {
  return (
    <ol className="home-membership__workflow-steps">
      {steps.map((step, index) => (
        <li key={step.text}>
          <span className="home-membership__workflow-icon" aria-hidden="true">
            {step.icon}
          </span>
          <span className="home-membership__workflow-step-text">
            <span className="home-membership__workflow-step-number">{index + 1}.</span> {step.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

export const PublicVsMembershipSection: React.FC = () => (
  <section
    id="public-vs-membership"
    className="home-section home-section--muted home-membership"
    aria-labelledby="public-vs-membership-heading"
  >
    <div className="home-section__container">
      <header className="home-membership__hero">
        <h2 id="public-vs-membership-heading" className="home-membership__title">
          {MEMBERSHIP_HERO.title}
        </h2>
        {MEMBERSHIP_HERO.paragraphs.map((paragraph) => (
          <p key={paragraph} className="home-membership__subtitle">
            {paragraph}
          </p>
        ))}
        <aside className="home-membership__goal-callout" aria-label="Membership goal">
          <p className="home-membership__goal-label">{MEMBERSHIP_HERO.goalCallout.label}</p>
          <p className="home-membership__goal-text">
            {MEMBERSHIP_HERO.goalCallout.prefix}
            <strong>{MEMBERSHIP_HERO.goalCallout.emphasis}</strong>
            {MEMBERSHIP_HERO.goalCallout.suffix}
          </p>
        </aside>
      </header>

      <section className="home-membership__cost-table" aria-labelledby="membership-cost-heading">
        <h3 id="membership-cost-heading" className="home-membership__section-title home-membership__section-title--sr-friendly">
          Membership cost comparison
        </h3>
        <p className="home-membership__section-intro">{MEMBERSHIP_COST_COMPARISON.intro}</p>
        <div className="home-membership__table-wrap">
          <table className="home-membership__table">
            <thead>
              <tr>
                {MEMBERSHIP_COST_COMPARISON.columns.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MEMBERSHIP_COST_COMPARISON.rows.map((row) => (
                <tr key={row.population}>
                  <th scope="row">{row.population}</th>
                  <td>{formatUsd(row.ghgInventoryCost)}</td>
                  <td>{formatUsd(row.affiliateFee)}</td>
                  <td>{row.affiliatePct}</td>
                  <td>{formatUsd(row.capCost)}</td>
                  <td>{formatUsd(row.regularFee)}</td>
                  <td>{row.regularPct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="home-membership__table-note">{MEMBERSHIP_COST_COMPARISON.disclaimer}</p>
        <p className="home-membership__source">
          Source:{' '}
          <a href={NEEDS_ASSESSMENT_PDF_URL} target="_blank" rel="noopener noreferrer">
            {NEEDS_ASSESSMENT_SOURCE_LABEL}
          </a>
        </p>
      </section>

      <section className="home-membership__starting-point" aria-labelledby="better-starting-point-heading">
        <h3 id="better-starting-point-heading" className="home-membership__section-title">
          {BETTER_STARTING_POINT.title}
        </h3>
        {BETTER_STARTING_POINT.paragraphs.map((paragraph) => (
          <p key={paragraph} className="home-membership__section-text">
            {paragraph}
          </p>
        ))}
      </section>

      <section className="home-membership__workflow" aria-labelledby="workflow-comparison-heading">
        <h3 id="workflow-comparison-heading" className="home-membership__section-title">
          {WORKFLOW_COMPARISON.title}
        </h3>
        <div className="home-membership__workflow-grid">
          <article className="home-membership__workflow-card home-membership__workflow-card--traditional">
            <h4>{WORKFLOW_COMPARISON.traditional.label}</h4>
            <WorkflowSteps steps={WORKFLOW_COMPARISON.traditional.items} />
          </article>
          <article className="home-membership__workflow-card home-membership__workflow-card--platform">
            <h4>{WORKFLOW_COMPARISON.climatePlans.label}</h4>
            <WorkflowSteps steps={WORKFLOW_COMPARISON.climatePlans.items} />
          </article>
        </div>
      </section>

      <section className="home-membership__why" aria-labelledby="why-agencies-join-heading">
        <h3 id="why-agencies-join-heading" className="home-membership__section-title">
          {WHY_AGENCIES_JOIN.title}
        </h3>
        <div className="home-membership__benefit-grid">
          {WHY_AGENCIES_JOIN.items.map((item) => (
            <article key={item} className="home-membership__benefit-card">
              <p>{item}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-membership__tiers" aria-labelledby="membership-tiers-heading">
        <h3 id="membership-tiers-heading" className="home-membership__section-title">
          Membership Options
        </h3>
        <div className="home-compare home-compare--three">
          <article className="home-compare__column home-compare__column--public">
            <p className="home-compare__tier">{PUBLIC_ACCESS.tierLabel}</p>
            <h4 className="home-compare__heading">{PUBLIC_ACCESS.heading}</h4>
            <p className="home-compare__intro">{PUBLIC_ACCESS.description}</p>
            <ul className="home-compare__list home-compare__list--compact">
              {PUBLIC_ACCESS.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="home-compare__column home-compare__column--affiliate">
            <p className="home-compare__tier">{AFFILIATE_MEMBERSHIP.tierLabel}</p>
            <h4 className="home-compare__heading home-compare__heading--emphasis">
              {AFFILIATE_MEMBERSHIP.heading}
            </h4>
            <p className="home-compare__price">{AFFILIATE_MEMBERSHIP.priceLabel}</p>
            <p className="home-compare__intro">{AFFILIATE_MEMBERSHIP.intro}</p>
            <ul className="home-compare__list home-compare__list--compact">
              {AFFILIATE_MEMBERSHIP.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="home-compare__column home-compare__column--regular">
            <p className="home-compare__tier">{REGULAR_MEMBERSHIP.tierLabel}</p>
            <h4 className="home-compare__heading home-compare__heading--emphasis">
              {REGULAR_MEMBERSHIP.heading}
            </h4>
            <p className="home-compare__price">{REGULAR_MEMBERSHIP.priceLabel}</p>
            <p className="home-compare__intro">{REGULAR_MEMBERSHIP.intro}</p>
            <ul className="home-compare__list home-compare__list--compact">
              {REGULAR_MEMBERSHIP.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
        <p className="home-membership__roadmap-note">{MEMBERSHIP_ROADMAP_NOTE}</p>
      </section>
    </div>
  </section>
);
